import { useMemo, useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import DashboardCard, { EmptyState, SkeletonCard } from './DashboardCard';
import {
  groupHeldByResident,
  type FacilityHeldDietOrder,
} from '../../api/dietOrderLockApi';
import HeldEhrChangesReview from '../people/HeldEhrChangesReview';
import { heldChangesTitle } from '../people/dietOrderProvenance';
import './HeldEhrChangesCard.css';

interface Props {
  facilityId: number;
  /** Facility EHR title ("EHR", "PCC", ...). */
  ehrTitle: string;
  /** Singular resident label ("Resident", "Patient", ...). */
  residentLabel: string;
  /**
   * Set when the card can't apply here (EHR off, diet order locking off,
   * no rights to diet changes). The card still shows, with this reason, so
   * it is never missing from the Dashboard.
   */
  unavailableReason?: string | null;
  rows: FacilityHeldDietOrder[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** PSI staff only: why the card is on this Dashboard, in its info tooltip. */
  psiNote?: string;
}

function fmtReceived(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Residents whose diet order is locked and who have diet changes from
 * the EHR waiting for review (tblDiet_Flucs_Lock, Completed = 0). The
 * WinForms Diet Order Changes dashboard lists these as "Pending Diet"
 * rows with Apply Pending Diet; here each resident opens the same
 * review the Diet History tab uses (use the new order, or keep the
 * current one).
 *
 * The Dashboard mounts it only where the facility holds EHR diet changes
 * (heldChangesApply). It then renders rows when something is held, "none
 * waiting" when the queue is empty, the reason when the user's role can't
 * see them, and a retry when the check failed.
 */
export default function HeldEhrChangesCard({
  facilityId,
  ehrTitle,
  residentLabel,
  unavailableReason,
  rows,
  isLoading,
  isError,
  onRetry,
  psiNote,
}: Props) {
  const title = heldChangesTitle(ehrTitle);
  const residents = useMemo(() => groupHeldByResident(rows ?? []), [rows]);

  // The resident under review stays set after the dialog closes so the
  // AIM dialogs that follow a save keep their host mounted.
  const [review, setReview] = useState<{ patientId: number; fullName: string } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const reviewHeld = useMemo(
    () => (review ? residents.find((r) => r.patientId === review.patientId)?.orders ?? [] : []),
    [residents, review],
  );

  const resident = residentLabel || 'Resident';

  const infoIcon = psiNote ? (
    <Tooltip title={psiNote}>
      <button type="button" className="held-ehr-card-info" aria-label={psiNote}>
        <i className="bi bi-info-circle" aria-hidden="true" />
      </button>
    </Tooltip>
  ) : undefined;

  let body: ReactNode;
  if (unavailableReason) {
    body = (
      <DashboardCard icon="bi-hourglass-split" title={title} headerRight={infoIcon}>
        <EmptyState message={unavailableReason} />
      </DashboardCard>
    );
  } else if (isLoading) {
    body = <SkeletonCard icon="bi-hourglass-split" title={title} />;
  } else if (isError) {
    body = (
      <DashboardCard severity="alert" icon="bi-hourglass-split" title={title} headerRight={infoIcon}>
        <Alert
          severity="error"
          role="alert"
          sx={{ m: 1 }}
          action={(
            <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
              Try again
            </Button>
          )}
        >
          Couldn&apos;t check for held {ehrTitle} diet changes.
        </Alert>
      </DashboardCard>
    );
  } else if (residents.length > 0) {
    body = (
      <DashboardCard severity="warning" icon="bi-hourglass-split" title={title} count={residents.length} headerRight={infoIcon}>
        <div className="held-ehr-card-scroll">
          <table className="held-ehr-card-table">
            <caption className="sr-only">
              {title}: one row per {resident.toLowerCase()}, most recently received first
            </caption>
            <thead>
              <tr>
                <th scope="col">{resident}</th>
                <th scope="col" className="held-ehr-card-num">Held</th>
                <th scope="col">Newest held diet order</th>
                <th scope="col">Received</th>
                <th scope="col"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {residents.map((r) => (
                <tr key={r.patientId}>
                  <th scope="row" className="held-ehr-card-name" id={`held-ehr-${r.patientId}`}>{r.fullName}</th>
                  <td className="held-ehr-card-num">{r.orders.length}</td>
                  <td>{r.newest.newDietOrderText || '(none)'}</td>
                  <td className="held-ehr-card-date">{fmtReceived(r.newest.date_Downloaded)}</td>
                  <td className="held-ehr-card-action">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setReview({ patientId: r.patientId, fullName: r.fullName });
                        setReviewOpen(true);
                      }}
                      // Named by its word, described by whose held changes it opens.
                      aria-describedby={`held-ehr-${r.patientId}`}
                      sx={{ textTransform: 'none', minWidth: 0 }}
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardCard>
    );
  } else {
    body = (
      <DashboardCard icon="bi-hourglass-split" title={title} headerRight={infoIcon}>
        <EmptyState message={`No held ${ehrTitle} diet changes waiting for review.`} />
      </DashboardCard>
    );
  }

  return (
    <>
      <div className="held-ehr-card-host">{body}</div>

      {review && (
        <HeldEhrChangesReview
          key={review.patientId}
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          facilityId={facilityId}
          patientId={review.patientId}
          patientFullName={review.fullName}
          held={reviewHeld}
        />
      )}
    </>
  );
}
